import { VpButton, VpCheckbox, VpCheckboxInput, VpDrawer, VpDrawerBody, VpDrawerCloseButton, VpDrawerDialog, VpDrawerFooter, VpDrawerHeader } from '@vtmn-play/react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { ALL_SELECTABLE_DATA_POINTS, LIST_SELECTABLE_DATA_POINTS } from '@/contexts/products/customViewConfig'
import { useCustomViewPanel } from '@/contexts/products/useCustomViewPanel'
import { useProductsContext } from '@/contexts/products/useProductsContext'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { BASE, buildSections } from './customViewPanel.sections'
import { InfoTooltip } from './InfoTooltip'
import './custom-view-panel.scss'

export function CustomViewPanel() {
  const { t } = useTranslation()
  const { season } = useParams<{ season: string }>()
  const {
    viewMode,
    customViewTarget,
    customViewSelections,
    isCustomViewOpen,
    closeCustomView,
    onCustomViewSelections,
  } = useProductsContext()

  const {
    checked,
    isItemSelected,
    toggleItem,
    handleSelectAll,
    handleSave,
    handleCancel,
    draftSelectedItems,
  } = useCustomViewPanel({
    currentSelections: customViewSelections,
    onSave: onCustomViewSelections,
    onClose: closeCustomView,
    selectableItems: customViewTarget === 'list' ? LIST_SELECTABLE_DATA_POINTS : ALL_SELECTABLE_DATA_POINTS,
  })

  const triggerElementRef = useRef<HTMLElement | null>(null)
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  const { trapRef, activate, deactivate, setReturnFocus } = useFocusTrap<HTMLDialogElement>({
    hideBackground: true,
  })

  useEffect(() => {
    if (isCustomViewOpen) {
      triggerElementRef.current = document.activeElement as HTMLElement
      setReturnFocus(triggerElementRef.current)

      // Small delay to ensure drawer is rendered
      const timer = setTimeout(() => {
        activate()
      }, 0)

      return () => clearTimeout(timer)
    }
    else {
      deactivate()
    }
  }, [isCustomViewOpen, activate, deactivate, setReturnFocus])

  const handleSaveWithTracking = () => {
    trackAnalyticsEvent(customViewTarget === 'list'
      ? ANALYTICS_EVENTS.PRODUCTS_LIST_CUSTOM_VIEW_APPLIED
      : ANALYTICS_EVENTS.PRODUCT_GALLERY_CUSTOM_VIEW_APPLIED, {
      season,
      active_columns: draftSelectedItems,
      active_columns_count: draftSelectedItems.length,
    })
    handleSave()
  }

  const sections = buildSections(viewMode, customViewTarget)

  useEffect(() => {
    const checkbox = selectAllCheckboxRef.current
    if (checkbox) {
      checkbox.indeterminate = checked === 'indeterminate'
      if (checkbox.indeterminate) {
        checkbox.checked = false
      }
    }
  })

  return (
    <VpDrawer
      closeOnClickOutside
      open={isCustomViewOpen}
      onOpen={() => { /* no-op */ }}
      onClose={handleCancel}
    >
      <VpDrawerDialog
        ref={trapRef}
        aria-label={t(`${BASE}.title`)}
        data-testid="custom-view-panel"
      >
        <VpDrawerHeader>
          {t(`${BASE}.title`)}
          <VpDrawerCloseButton
            aria-label={t('close')}
            data-testid="custom-view-panel__close"
          />
        </VpDrawerHeader>
        <VpDrawerBody>
          <div className="custom-view-panel__select-all">
            <VpCheckbox
              id="custom-view-select-all"
              name="custom-view-select-all"
              checked={checked}
              onChange={handleSelectAll}
              inputSlot={(
                <VpCheckboxInput
                  className="vp-checkbox__input"
                  data-part="input"
                  data-testid="custom-view-panel__select-all"
                  ref={selectAllCheckboxRef}
                />
              )}
            >
              {t(`${BASE}.select-all`)}
            </VpCheckbox>
          </div>

          {sections.map(section => (
            <div key={section.titleKey} className="custom-view-panel__section">
              <span className="custom-view-panel__section-title">
                {t(section.titleKey)}
              </span>
              <ul className="custom-view-panel__field-list">
                {section.fields.map((fieldConfig, idx) => {
                  if (fieldConfig.type === 'mandatory') {
                    return (
                      <li
                        key={fieldConfig.labelKey}
                        className="custom-view-panel__field custom-view-panel__field--mandatory"
                      >
                        <VpCheckbox
                          id={`custom-view-field-mandatory-${idx}`}
                          name={`custom-view-field-mandatory-${idx}`}
                          defaultChecked
                          disabled
                          isFocusable={false}
                        >
                          {t(fieldConfig.labelKey)}
                        </VpCheckbox>
                      </li>
                    )
                  }

                  if (fieldConfig.type === 'lockedOff') {
                    return (
                      <li
                        key={fieldConfig.labelKey}
                        className="custom-view-panel__field custom-view-panel__field--locked"
                      >
                        <VpCheckbox
                          id={`custom-view-field-locked-${idx}`}
                          name={`custom-view-field-locked-${idx}`}
                          defaultChecked={false}
                          disabled
                          isFocusable={false}
                        >
                          {t(fieldConfig.labelKey)}
                        </VpCheckbox>
                        <InfoTooltip reason={fieldConfig.reason} />
                      </li>
                    )
                  }

                  return (
                    <li
                      key={fieldConfig.key}
                      className="custom-view-panel__field"
                    >
                      <VpCheckbox
                        id={`custom-view-field_${fieldConfig.key}`}
                        name={`custom-view-field_${fieldConfig.key}`}
                        checked={isItemSelected(fieldConfig.key)}
                        onChange={() => toggleItem(fieldConfig.key)}
                        data-testid={`custom-view-field_${fieldConfig.key}`}
                      >
                        {t(fieldConfig.labelKey)}
                      </VpCheckbox>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </VpDrawerBody>
        <VpDrawerFooter className="custom-view-panel__footer">
          <VpButton
            variant="secondary"
            onClick={handleCancel}
            aria-label={t('aria-label.custom-view-cancel')}
            data-testid="custom-view-panel__cancel"
          >
            {t('cancel')}
          </VpButton>
          <VpButton
            onClick={handleSaveWithTracking}
            aria-label={t('aria-label.custom-view-save')}
            data-testid="custom-view-panel__save"
          >
            {t('save')}
          </VpButton>
        </VpDrawerFooter>
      </VpDrawerDialog>
    </VpDrawer>
  )
}
